import { Box, Stack, Paper, Skeleton, Typography } from '@mui/material';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTaxReportQueries } from './hooks/useTaxReportsQueries';
import { usePaymentQueries } from '../../hooks/usePaymentQueries';
import { useTaxReportService } from '../../services/tax-report.service';
import { usePricing } from '../../hooks/usePricing';
import { packages } from '../plans/constants';
import { convertCentsToEuros } from '../../services/transformers';
import type { Package } from '../../services/payment.types';
import { useApiClient } from '../../services/api';
import { useTransactionalEmailService } from '../../services/transactionalEmailService';
import { environment } from '../../config/environment';
import { PaymentResultBanner } from './components/PaymentResultBanner';
import { useUserPlanStatusWithTransactionLimit } from '../../hooks/useUserPlanStatusWithTransactionLimit';
import { useTransactionNavigation } from '../transactions/hooks/useTransactionNavigation';
import { TransactionIssueType } from '../../services/transaction.types';
import { useTransactionService, DetailsType } from '../../services/transaction.service';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../utils/queryKeys';
import { useIgnoredIssues } from '../smart-check/hooks/useIgnoredIssues';
import { useRecalculation } from '../../context/RecalculationStatusContext';
import RecalculationAlert from './components/RecalculationAlert';

// Components
import YearSelectorCards from './components/YearSelectorCards';
import UnifiedTaxSummaryBox from './components/UnifiedTaxSummaryBox';
import DownloadFilesBox from './components/DownloadFilesBox';
import TaxFreeIncomeBox from './components/TaxFreeIncomeBox';
import OtherTransactionsBox from './components/OtherTransactionsBox';
import PurchaseActionsBox from './components/PurchaseActionsBox';

// Before this year we allow kapso download, after (and including) we do not
const KAPSO_CUTOFF_YEAR = 2025;

const TaxReportPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { navigateToTransactions } = useTransactionNavigation();
     const { status: recalculationStatus } = useRecalculation();
    const isRecalculating = !!recalculationStatus?.run || !!recalculationStatus?.new;
    const { createCheckoutSession } = usePaymentQueries();
    const { pricing } = usePricing('DE');
    const location = useLocation();
    const reactQueryClient = useQueryClient();
    const apiClient = useApiClient();
    const emailService = useTransactionalEmailService();
    
    // Payment status state
    const [paymentStatus, setPaymentStatus] = useState<'success' | 'cancel' | null>(null);
    const [paymentData, setPaymentData] = useState<any>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    
    // Payment verification tracking to prevent duplicates
    const verificationInProgress = useRef<string | null>(null);
    const verifiedSessions = useRef<Set<string>>(new Set());
    
    const { isPurchased, hasEnoughTransactionLimit } = useUserPlanStatusWithTransactionLimit();
    // Remove recalculation status import if not needed for other logic

    const {
        taxReports,
        selectedYear,
        setSelectedYear,
        taxPersonalData,
        yearOptions,
        downloadPdf: handleDownloadPdf,
        downloadExcel: handleDownloadExcel,
        downloadKapsoPdf: handleDownloadKapsoPdf,
        isLoading,
        isCriticalLoading,
        isDownloadLoading,
    } = useTaxReportQueries();

    // Extract individual loading states for better UX
    const reportsLoading = taxReports.length === 0 && isLoading;

    const transactionService = useTransactionService();
    const taxReportService = useTaxReportService();

    // We no longer need to fetch stats for all years since year cards are simplified
    // Only keep the Smart Check issues query which is still needed for warning indicators

    // Get Smart Check issues for all years
    const { data: smartCheckIssues, isLoading: isLoadingSmartCheckIssues } = useQuery({
        queryKey: [QUERY_KEYS.ALL_TIME_ISSUES],
        queryFn: () => transactionService.getAllTimeIssueCount(DetailsType.ONLY_ISSUES),
    });

    // Get ignored issues from Firebase to exclude them from counts
    const { data: allIgnoredIssues } = useIgnoredIssues();

    // Process Smart Check issues and group by year, excluding ignored issues
    const getIssueCountByYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let issueCount = 0;
        const costBasisIssues = smartCheckIssues.missingCostBasisTransactions || [];
        const missingPriceIssues = smartCheckIssues.missingPriceIncomeTransactions || [];
        
        // Count cost basis issues for this year (exclude ignored issues)
        for (const issue of costBasisIssues) {
            // Cost basis issues use sold_at field (can be array)
            const issueYear = new Date(Array.isArray(issue.sold_at) ? issue.sold_at[0] : issue.sold_at).getFullYear().toString();
            if (issueYear === year) {
                // Only count if not ignored
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        // Count missing price issues for this year (exclude ignored issues)
        for (const issue of missingPriceIssues) {
            // Missing price issues use time field (no *1000 needed as it's already in milliseconds)
            const issueYear = new Date(issue.time).getFullYear().toString();
            if (issueYear === year) {
                // Only count if not ignored
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        return issueCount;
    };

    // Calculate total issue count for selected year (both types)
    const totalIssueCountForSelectedYear = getIssueCountByYear(selectedYear);

    // Calculate separate issue counts by type for selected year
    const getCostBasisIssueCountByYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let issueCount = 0;
        const costBasisIssues = smartCheckIssues.missingCostBasisTransactions || [];
        
        for (const issue of costBasisIssues) {
            // Use same logic as Smart Check: prioritize taxYear property, fall back to date calculation
            const issueYear = (issue.taxYear || new Date(Array.isArray(issue.sold_at) ? issue.sold_at[0] : issue.sold_at).getFullYear()).toString();
            if (issueYear === year) {
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        return issueCount;
    };

    const getMissingPriceIssueCountByYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let issueCount = 0;
        const missingPriceIssues = smartCheckIssues.missingPriceIncomeTransactions || [];
        
        for (const issue of missingPriceIssues) {
            // Use same logic as Smart Check: prioritize taxYear property, fall back to date calculation
            const issueYear = (issue.taxYear || new Date(issue.time).getFullYear()).toString();
            if (issueYear === year) {
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        return issueCount;
    };

    const costBasisCountForSelectedYear = getCostBasisIssueCountByYear(selectedYear);
    const missingPriceCountForSelectedYear = getMissingPriceIssueCountByYear(selectedYear);

    // Calculate savings potential ONLY from cost basis issues (missing price issues don't have reliable savings data)
    const calculateSavingsPotentialForYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let totalSavings = 0;
        const costBasisIssues = smartCheckIssues.missingCostBasisTransactions || [];
        
        // Only calculate savings from missing cost basis issues (they have reliable gain property)
        for (const issue of costBasisIssues) {
            // Use same logic as Smart Check: prioritize taxYear property, fall back to date calculation
            const issueYear = (issue.taxYear || new Date(Array.isArray(issue.sold_at) ? issue.sold_at[0] : issue.sold_at).getFullYear()).toString();
            if (issueYear === year) {
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored && issue.gain !== undefined && issue.gain !== null) {
                    // Use absolute value of gain as potential tax savings
                    totalSavings += Math.abs(issue.gain);
                }
            }
        }
        
        return totalSavings;
    };

    const totalSavingsPotentialForSelectedYear = calculateSavingsPotentialForYear(selectedYear);

    // Conditional logging
    useEffect(() => {
        console.log(`[TaxReportPageV2] Year ${selectedYear}: Cost basis issues: ${costBasisCountForSelectedYear}, Missing price issues: ${missingPriceCountForSelectedYear} | Savings: ${totalSavingsPotentialForSelectedYear}`);
    }, [selectedYear, costBasisCountForSelectedYear, missingPriceCountForSelectedYear, totalSavingsPotentialForSelectedYear]);

    // Dynamic summary queries for all years - only fetch if purchased
    const summaryQueries = useQueries({
        queries: yearOptions.map(year => {
            const report = taxReports.find(r => r.tax_year === year);
            const yearIsPaid = isPurchased(year);
            return {
                queryKey: ['reportSummary', year, report?.id],
                queryFn: () => taxReportService.reportsFullExportSummary(report!.id),
                enabled: !!report?.id && yearIsPaid, // Only fetch if year is purchased
            };
        })
    });

    // Transaction stats queries for all years - only fetch if purchased
    const transactionStatsQueries = useQueries({
        queries: yearOptions.map(year => {
            const report = taxReports.find(r => r.tax_year === year);
            const yearIsPaid = isPurchased(year);
            return {
                queryKey: ['transactionStats', year, report?.id],
                queryFn: () => transactionService.getStats(year, report!.id),
                enabled: !!report?.id && yearIsPaid, // Only fetch if year is purchased
            };
        })
    });

    // Transaction count queries for ALL years (for pricing calculation) - not limited to purchased years
    const transactionCountQueries = useQueries({
        queries: yearOptions.map(year => {
            const report = taxReports.find(r => r.tax_year === year);
            return {
                queryKey: ['transactionCount', year, report?.id],
                queryFn: () => transactionService.getStats(year, report!.id),
                enabled: !!report?.id, // Fetch for all years that have reports
            };
        })
    });

    // Map summary results to year-indexed object
    const allYearsSummaries = yearOptions.reduce((acc, year, index) => {
        acc[year] = {
            summary: summaryQueries[index]?.data,
            isLoading: summaryQueries[index]?.isLoading || false
        };
        return acc;
    }, {} as Record<string, { summary: any; isLoading: boolean }>);

    // Map transaction stats results to year-indexed object
    const allYearsStats = yearOptions.reduce((acc, year, index) => {
        acc[year] = {
            stats: transactionStatsQueries[index]?.data,
            isLoading: transactionStatsQueries[index]?.isLoading || false
        };
        return acc;
    }, {} as Record<string, { stats: any; isLoading: boolean }>);

    // Map transaction count results to year-indexed object (for pricing)
    const allYearsTransactionCounts = yearOptions.reduce((acc, year, index) => {
        const transactionData = transactionCountQueries[index]?.data;
        acc[year] = {
            count: transactionData?.totalTransactions || 0,
            isLoading: transactionCountQueries[index]?.isLoading || false
        };
        return acc;
    }, {} as Record<string, { count: number; isLoading: boolean }>);

    // Get preloaded data for selected year
    const reportSummary = allYearsSummaries[selectedYear]?.summary;
    const transactionStats = allYearsStats[selectedYear]?.stats;
    const isLoadingSelectedYear = allYearsSummaries[selectedYear]?.isLoading || allYearsStats[selectedYear]?.isLoading;
    
    // Show loading when:
    // 1. Initial tax reports are loading (reportsLoading)
    // 2. No years available yet (yearOptions.length === 0)
    // 3. Selected year data is still loading
    // 4. Recalculation is in progress (data might be stale)
    const shouldShowLoading = reportsLoading || yearOptions.length === 0 || isLoadingSelectedYear || isRecalculating;

    // Calculate canLoadDetails with preloaded transaction stats
    const canLoadDetails = !!(
        selectedYear &&
        transactionStats &&
        isPurchased(selectedYear) &&
        hasEnoughTransactionLimit(selectedYear, transactionStats.totalTransactions)
    );

    // Calculate taxable income from summary data (same logic as TaxSummaryBox)
    const calculateTaxableIncome = (summaryData: any[]): number => {
        if (!summaryData || summaryData.length === 0) return 0;

        // Helper function to find a value in the nested structure with multi-language support
        const findValueByLabel = (group: any, targetLabels: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    // Check if the label matches any of the target labels
                    if (targetLabels.some(label => entry.kv.label === label)) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByLabel(entry.g, targetLabels);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };

        let totalAmount = 0;
        
        // Process each summary data item
        for (const summary of summaryData) {
            if (summary.g) {
                // Find values for specific income types (support both German and English labels)
                const cryptoSalesGains = findValueByLabel(
                    summary.g, 
                    [
                        'Einkünfte aus privaten Veräußerungsgeschäften nach § 23 EStG', // German
                        'Private sale income according to Art. 23 German income tax act' // English
                    ]
                );
                
                const otherIncome = findValueByLabel(
                    summary.g, 
                    [
                        'Sonstige Einkünfte nach § 22 Nr. 3 EStG', // German
                        'Other income according to Art. 22 No. 3 German income tax act' // English
                    ]
                );
                
                const marginDerivativesGains = findValueByLabel(
                    summary.g, 
                    [
                        'Kapitalerträge aus Margin- und Derivategeschäften', // German
                        'Total gains from margin, derivatives, futures' // English
                    ]
                );

                totalAmount += cryptoSalesGains + otherIncome + marginDerivativesGains;
            }
        }
        
        return totalAmount;
    };

    // Extract income breakdown from summary data
    const extractIncomeBreakdown = (summaryData: any[]) => {
        if (!summaryData || summaryData.length === 0) return [];

        // Helper function to find a value in the nested structure with multi-language support
        const findValueByLabel = (group: any, targetLabels: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    // Check if the label matches any of the target labels
                    if (targetLabels.some(label => entry.kv.label === label)) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByLabel(entry.g, targetLabels);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };

        let cryptoSalesGains = 0;
        let otherIncome = 0;
        let marginDerivativesGains = 0;
        
        // Process each summary data item to extract values
        for (const summary of summaryData) {
            if (summary.g) {
                cryptoSalesGains += findValueByLabel(
                    summary.g, 
                    [
                        'Einkünfte aus privaten Veräußerungsgeschäften nach § 23 EStG', // German
                        'Private sale income according to Art. 23 German income tax act' // English
                    ]
                );
                
                otherIncome += findValueByLabel(
                    summary.g, 
                    [
                        'Sonstige Einkünfte nach § 22 Nr. 3 EStG', // German
                        'Other income according to Art. 22 No. 3 German income tax act' // English
                    ]
                );
                
                marginDerivativesGains += findValueByLabel(
                    summary.g, 
                    [
                        'Kapitalerträge aus Margin- und Derivategeschäften', // German
                        'Total gains from margin, derivatives, futures' // English
                    ]
                );
            }
        }
        
        return [
            {
                label: 'Private Veräußerungsgeschäfte (§ 23 EStG)',
                amount: cryptoSalesGains,
                translationKey: 'taxReports.privateSalesIncome'
            },
            {
                label: 'Sonstige Einkünfte (§ 22 Nr. 3 EStG)',
                amount: otherIncome,
                translationKey: 'taxReports.otherIncome'
            },
            {
                label: 'Kapitalerträge aus Margin-/Derivategeschäften',
                amount: marginDerivativesGains,
                translationKey: 'taxReports.capitalGains'
            }
        ];
    };

    // Extract additional tax data from summary data
    const extractAdditionalTaxData = (summaryData: any[]) => {
        if (!summaryData || summaryData.length === 0) return { otherTransactions: [], taxFreeIncome: [] };

        // Helper function to find a value in the nested structure with multi-language support
        const findValueByLabel = (group: any, targetLabels: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    // Check if the label matches any of the target labels
                    if (targetLabels.some(label => entry.kv.label === label)) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByLabel(entry.g, targetLabels);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };


        // Helper function to search for labels containing keywords
        const findValueByKeywords = (group: any, keywords: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    const label = entry.kv.label.toLowerCase();
                    if (keywords.some(keyword => label.includes(keyword.toLowerCase()))) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByKeywords(entry.g, keywords);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };

        let donationsReceived = 0;
        let donationsSent = 0;
        let lostStolenAssets = 0;
        let nonTaxableSalesIncome = 0;
        let nonTaxableOtherIncome = 0;
        
        // Process each summary data item to extract values
        for (const summary of summaryData) {
            if (summary.g) {
                
                // Look for donations - search by keywords
                donationsReceived += findValueByKeywords(summary.g, ['spende erhalten', 'donation received', 'schenkung erhalten']);
                donationsSent += findValueByKeywords(summary.g, ['spende', 'donation', 'schenkung']);
                
                // Look for lost/stolen assets
                lostStolenAssets += findValueByKeywords(summary.g, ['verlust', 'gestohlen', 'diebstahl', 'verloren', 'stolen', 'lost']);
                
                // Look for non-taxable income - support both German and English labels
                nonTaxableSalesIncome += findValueByLabel(summary.g, [
                    'Nicht steuerbare Einkünfte aus privaten Veräußerungsgeschäften', // German
                    'Private sale income non taxable' // English
                ]);
                nonTaxableOtherIncome += findValueByLabel(summary.g, [
                    'Nicht steuerbare sonstige Einkünfte', // German
                    'Other income non taxable' // English
                ]);
            }
        }
        
        return {
            otherTransactions: [
                {
                    label: 'Donations received',
                    amount: donationsReceived,
                    translationKey: 'taxReports.donationsReceived'
                },
                {
                    label: 'Donations sent',
                    amount: donationsSent,
                    translationKey: 'taxReports.donationsSent'
                },
                {
                    label: 'Lost/stolen assets',
                    amount: lostStolenAssets,
                    translationKey: 'taxReports.lostStolen'
                }
            ],
            taxFreeIncome: [
                {
                    label: 'Non-taxable sales income',
                    amount: nonTaxableSalesIncome,
                    translationKey: 'taxReports.nonTaxableSales'
                },
                {
                    label: 'Non-taxable other income',
                    amount: nonTaxableOtherIncome,
                    translationKey: 'taxReports.nonTaxableOther'
                }
            ]
        };
    };

    // Handle navigation to transactions with specific issue type
    const handleNavigateToTransactions = (issueType: TransactionIssueType) => {
        navigateToTransactions({
            flags: [issueType],
            dates: {
                start: `${selectedYear}-01-01`,
                end: `${selectedYear}-12-31`,
            },
        });
    };

    // Handle navigation to Smart Check with all issues for selected year
    const handleNavigateToSmartCheck = () => {
        navigate(`/smart-check?year=${selectedYear}&dateStart=${selectedYear}-01-01&dateEnd=${selectedYear}-12-31`);
    };

    // Merge pricing data with package constants (exact copy from plans page)
    const packagesWithPricing = useMemo(() => {
        if (!pricing?.packages) return packages;
        
        return packages.map((pkg) => ({
            ...pkg,
            price: pricing.packages[pkg.id as keyof typeof pricing.packages]?.price 
                ? convertCentsToEuros(pricing.packages[pkg.id as keyof typeof pricing.packages].price)
                : pkg.price,
            transactions: pricing.packages[pkg.id as keyof typeof pricing.packages]?.transactions ?? pkg.transactions,
        }));
    }, [pricing?.packages]);

    // Package selection function (exact copy from plans page)
    const getPackageForTransactions = useCallback((transactionCount: number): Package => {
        const suitablePackages = packagesWithPricing
            .filter(pkg => pkg.transactions >= transactionCount)
            .sort((a, b) => a.price - b.price);
        
        // Return the cheapest suitable package, or enterprise if none can handle the transaction count
        return suitablePackages.length > 0 ? suitablePackages[0] : packagesWithPricing[packagesWithPricing.length - 1];
    }, [packagesWithPricing]);

    // Handle direct checkout for single year (exact replication of plans page logic)
    const handleBuyYear = useCallback(async (year: string) => {
        const transactionCount = transactionStats?.totalTransactions || 0;
        
        try {
            // Get the appropriate package for this transaction count
            const selectedPackage = getPackageForTransactions(transactionCount);
            
            const response = await createCheckoutSession({
                selectedYears: [
                    {
                        taxYear: parseInt(year),
                        package: selectedPackage,
                        expertReview: false,
                    }
                ],
                addons: {
                    portfolioInsights: { enabled: false },
                    taxOptimization: { enabled: false },
                    weDoEverything: { enabled: false },
                },
                source: 'tax_reports',
                returnUrl: `${window.location.origin}/tax-reports?payment=success`,
                cancelUrl: `${window.location.origin}/tax-reports?payment=cancel`,
            });
            
            if (response?.url) {
                window.location.href = response.url;
            } else {
                console.error('Failed to create checkout session - no URL returned');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
        }
    }, [transactionStats?.totalTransactions, getPackageForTransactions, createCheckoutSession]);

    // Handle direct checkout for all years (exact replication of plans page logic)
    const handleBuyAllYears = useCallback(async () => {
        try {
            // Build selectedYears array with appropriate packages for each year
            const selectedYears = yearOptions.map(year => {
                const yearTransactionCount = allYearsTransactionCounts[year]?.count || 0;
                const selectedPackage = getPackageForTransactions(yearTransactionCount);
                
                return {
                    taxYear: parseInt(year),
                    package: selectedPackage,
                    expertReview: false,
                };
            });

            const response = await createCheckoutSession({
                selectedYears,
                addons: {
                    portfolioInsights: { enabled: false },
                    taxOptimization: { enabled: false },
                    weDoEverything: { enabled: false },
                },
                source: 'tax_reports',
                returnUrl: `${window.location.origin}/tax-reports?payment=success`,
                cancelUrl: `${window.location.origin}/tax-reports?payment=cancel`,
            });
            
            if (response?.url) {
                window.location.href = response.url;
            } else {
                console.error('Failed to create checkout session - no URL returned');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
        }
    }, [yearOptions, allYearsTransactionCounts, getPackageForTransactions, createCheckoutSession]);

    // Navigate to plans page
    const handleShowAllPlans = () => {
        navigate(`/plans?year=${selectedYear}&transactions=${transactionStats?.totalTransactions}`);
    };

    // Prepare simplified year data for the cards (memoized to prevent unnecessary re-renders)
    // Must be called before any conditional returns to avoid React Hooks violation
    const yearDataMap = useMemo(() => {
        const dataMap: Record<string, any> = {};
        yearOptions.forEach(year => {
            const report = taxReports.find(r => r.tax_year === year);
            const yearIsPaid = isPurchased(year);
            
            // Get Smart Check issue count for this year
            const issueCount = getIssueCountByYear(year);
            
            // Get loading states for this year
            const yearSummaryLoading = allYearsSummaries[year]?.isLoading || false;
            const yearStatsLoading = allYearsStats[year]?.isLoading || false;
            const yearIsLoading = yearSummaryLoading || yearStatsLoading || isLoadingSmartCheckIssues;
            
            // Use the same logic as canLoadDetails for consistency
            const yearStats = allYearsStats[year]?.stats;
            const canLoadDetailsForYear = !!(
                yearStats &&
                yearIsPaid &&
                hasEnoughTransactionLimit(year, yearStats.totalTransactions)
            );
            
            dataMap[year] = {
                year,
                isPaid: canLoadDetailsForYear,
                hasIssues: issueCount > 0,
                issueCount: issueCount,
                isLoading: yearIsLoading,
            };
        });
        return dataMap;
    }, [
        // Only dependencies that affect year card data (NOT selectedYear)
        yearOptions,
        taxReports,
        smartCheckIssues,
        allIgnoredIssues,
        allYearsSummaries,
        allYearsStats,
        isLoadingSmartCheckIssues,
        isPurchased,
        hasEnoughTransactionLimit
    ]);


    // Create stable callback functions to minimize useEffect dependencies
    const handlePaymentVerification = useCallback(async (sessionId: string) => {
        try {
            // Mark verification as in progress and add to verified sessions immediately
            verificationInProgress.current = sessionId;
            verifiedSessions.current.add(sessionId);
            console.log('[PaymentSuccess] Starting verification for session:', sessionId);
            
            // Verify payment with backend
            const response = await apiClient.get(
                `${environment.expressApiUrl}/api/payment/verify-session/${sessionId}`
            );
            
            setPaymentData(response.data);
            setPaymentStatus('success');
            console.log('[PaymentSuccess] Payment verification completed successfully');
            
            // Send email separately - don't let email errors affect payment status
            try {
                const getFeatures = (details: any) => {
                    const features = [];
                    const metadata = details.metadata;

                    // Handle multiple tax report purchases using indexed format (same as Firebase)
                    const yearCount = parseInt(metadata.yearCount) || 0;
                    if (yearCount > 0) {
                        // Process each year using indexed metadata
                        for (let i = 0; i < yearCount; i++) {
                            const year = metadata[`taxYear_${i}`];
                            const packageId = metadata[`packageId_${i}`];
                            const totalTransactions = metadata[`totalTransactions_${i}`];
                            
                            if (packageId && year) {
                                if (totalTransactions) {
                                    // Include transaction count if available
                                    features.push(
                                        t(`plansData.packages.${packageId}.short_description`, {
                                            year: year,
                                            transactions: parseInt(totalTransactions) || 0,
                                        })
                                    );
                                } else {
                                    // Fallback to name + year format  
                                    features.push(
                                        t(`plansData.packages.${packageId}.name`) + ' ' + year
                                    );
                                }
                            }
                            
                            // Check expert review for this specific year
                            if (metadata[`expertReview_${i}`] === 'true') {
                                features.push(t('plansData.addons.expertReview.name', 'Expert Check') + ' ' + year);
                            }
                        }
                    }
                    
                    // Fallback for single tax report purchases (backward compatibility)
                    else if (metadata.packageId && metadata.taxYear) {
                        features.push(
                            t(`plansData.packages.${metadata.packageId}.short_description`, {
                                year: metadata.taxYear,
                                transactions: parseInt(metadata.totalTransactions) || 0,
                            })
                        );
                        
                        if (metadata.expertReview === 'true') {
                            features.push(
                                t('plansData.addons.expertReview.short_description', {
                                    year: metadata.taxYear,
                                })
                            );
                        }
                    }

                    return features;
                };
                
                const features = getFeatures(response.data).join(', ');
                await emailService.sendPurchaseSucceedEmail(features);
                console.log('[PaymentSuccess] Purchase email sent successfully');
            } catch (emailErr) {
                console.error('[PaymentSuccess] Error sending email (payment still successful):', emailErr);
                // Don't set payment error for email failures
            }
            
            // Invalidate userPlans query to refresh the sidebar
            await reactQueryClient.invalidateQueries({ queryKey: ['userPlans'] });
            
            // Clean URL after processing (remove payment parameters)
            setTimeout(() => {
                navigate('/tax-reports', { replace: true });
            }, 100);
            
        } catch (err) {
            console.error('[PaymentSuccess] Error verifying payment:', err);
            // Remove from verified sessions if payment verification failed
            verifiedSessions.current.delete(sessionId);
            setPaymentError('Failed to verify payment');
        } finally {
            // Clear in-progress flag
            verificationInProgress.current = null;
        }
    }, [apiClient, emailService, reactQueryClient, navigate, t]);

    const handlePaymentCancel = useCallback(() => {
        setPaymentStatus('cancel');
        console.log('[PaymentCancel] Payment was cancelled');
        
        // Clean URL after processing
        setTimeout(() => {
            navigate('/tax-reports', { replace: true });
        }, 100);
    }, [navigate]);

    // Payment status detection and handling with deduplication
    useEffect(() => {
        
        const searchParams = new URLSearchParams(location.search);
        const payment = searchParams.get('payment');
        const sessionId = searchParams.get('session_id');

        if (payment === 'success' && sessionId) {
            // Check if this session has already been verified or is currently being verified
            if (verifiedSessions.current.has(sessionId)) {
                console.log('[PaymentSuccess] Session already verified, skipping:', sessionId);
                return;
            }
            
            if (verificationInProgress.current === sessionId) {
                console.log('[PaymentSuccess] Verification already in progress for session:', sessionId);
                return;
            }

            handlePaymentVerification(sessionId);
        } else if (payment === 'cancel') {
            handlePaymentCancel();
        }
    }, [location.search, handlePaymentVerification, handlePaymentCancel]);

    // No blocking loading - always show page structure
    // Handle empty/loading states within the page layout

    return (
        <>
            {/* Payment Result Banner */}
            <PaymentResultBanner 
                status={paymentStatus}
                paymentData={paymentData}
                error={paymentError}
                onDismiss={() => {
                    setPaymentStatus(null);
                    setPaymentData(null);
                    setPaymentError(null);
                }}
            />
            
            <Box sx={{ 
                display: 'flex', 
                gap: { xs: 3, md: 6 }, 
                width: '100%',
                alignItems: 'flex-start',
                // Responsive behavior
                flexDirection: { xs: 'column', md: 'row' },
                py: 2
            }}>
                {/* Left Sidebar - Year Selection */}
                
                    <Box sx={{ 
                        flexShrink: 0,
                        width: { xs: '100%', md: 'auto' }
                    }}>
                        <YearSelectorCards
                            years={yearOptions}
                            selectedYear={selectedYear}
                            onYearSelect={setSelectedYear}
                            yearDataMap={yearDataMap}
                            isLoading={reportsLoading || isRecalculating}
                        />
                    </Box>
               

                {/* Right Content Area */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Recalculation Alert */}
                    <RecalculationAlert isRecalculating={isRecalculating} />
                    {shouldShowLoading ? (
                        // Show skeleton loading states
                        <Stack spacing={3}>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <Paper
                                    key={`content-skeleton-${index}`}
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        bgcolor: 'background.paper',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Skeleton width="200px" height="32px" />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Skeleton width="150px" height="24px" />
                                        <Skeleton width="80px" height="24px" />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Skeleton width="120px" height="24px" />
                                        <Skeleton width="60px" height="24px" />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Skeleton width="180px" height="24px" />
                                        <Skeleton width="70px" height="24px" />
                                    </Box>
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        <Stack spacing={4}>
                        {/* Unified Tax Summary Box - Shows total and breakdown in one modern component */}
                        <UnifiedTaxSummaryBox
                            totalAmount={reportSummary ? calculateTaxableIncome(reportSummary) : null}
                            details={reportSummary ? extractIncomeBreakdown(reportSummary) : []}
                            isPaid={canLoadDetails}
                            isLoading={isLoadingSelectedYear}
                            selectedYear={selectedYear}
                            issueCount={totalIssueCountForSelectedYear}
                            savingsPotential={totalSavingsPotentialForSelectedYear}
                            costBasisCount={costBasisCountForSelectedYear}
                            missingPriceCount={missingPriceCountForSelectedYear}
                            onGoToSmartCheck={handleNavigateToSmartCheck}
                        />


                        {/* Purchase CTA - Show AFTER they see the value proposition */}
                        {!canLoadDetails && (
                            <PurchaseActionsBox
                                selectedYear={selectedYear}
                                allYears={yearOptions.filter(year => !isPurchased(year))}
                                isPurchased={canLoadDetails}
                                transactionCounts={allYearsTransactionCounts}
                                onBuyYear={handleBuyYear}
                                onBuyAllYears={handleBuyAllYears}
                                onShowAllPlans={() => {
                                    // Navigate to plans page
                                    window.location.href = '/plans';
                                }}
                                isLoading={false}
                            />
                        )}

                        {/* Download Files Box - Show locked downloads for additional conversion pressure */}
                        <DownloadFilesBox
                            isPaid={canLoadDetails}
                            onDownloadPdf={() => handleDownloadPdf(`${t('taxReports.fileName')}_${selectedYear}.pdf`)}
                            onDownloadExcel={() => handleDownloadExcel(`${t('taxReports.fileName')}_${selectedYear}.xlsx`)}
                            {...(parseInt(selectedYear) < KAPSO_CUTOFF_YEAR && {
                                onDownloadForms: () => handleDownloadKapsoPdf(`${t('taxReports.fileName')}_Kapso_${selectedYear}.pdf`)
                            })}
                            isPreparingDownload={isDownloadLoading}
                        />

                        {/* Other Transactions Box */}
                        <OtherTransactionsBox
                            otherTransactions={reportSummary ? extractAdditionalTaxData(reportSummary).otherTransactions : []}
                            isPaid={canLoadDetails}
                            isLoading={isLoadingSelectedYear}
                        />
                        
                        {/* Tax Free Income Box */}
                        <TaxFreeIncomeBox
                            taxFreeIncome={reportSummary ? extractAdditionalTaxData(reportSummary).taxFreeIncome : []}
                            isPaid={canLoadDetails}
                            isLoading={isLoadingSelectedYear}
                        />
                    </Stack>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default TaxReportPage;